import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import { authFetch, handleApiResponse, describeApiError } from '../api/authFetch';

// How close to the bottom (in px) counts as "still following" the
// conversation - matches the "standard chat app" tolerance (Slack/Discord
// use a similar band) so a few px of sub-pixel scroll rounding doesn't
// falsely read as "the user scrolled away".
const NEAR_BOTTOM_THRESHOLD_PX = 120;

export const useChatHistory = ({ conversationId } = {}) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("error");
  const chatBoxRef = useRef(null);
  const navigate = useNavigate();

  // Tracks whether the reader is currently near the bottom of the scroll
  // container, kept up to date by a real scroll listener rather than
  // re-derived from post-render scrollHeight (a large chunk of streamed
  // content can grow scrollHeight by more than the threshold in one commit,
  // which would wrongly read as "scrolled away" even though the reader was
  // pinned to the bottom right before it arrived). This ref is the one the
  // auto-scroll effect below reads every render, so it's updated
  // synchronously on every scroll event with no throttling - any lag here
  // would show up as auto-follow occasionally yanking the reader down a
  // frame late.
  const isNearBottomRef = useRef(true);

  // UI-facing mirror of the ref above, for the "jump to latest" pill
  // (ChatArea/Chat.js) - unlike the ref, this drives a re-render, so it's
  // rAF-throttled to at most once per animation frame instead of updating
  // on every 'scroll' event (which can fire far more often than the screen
  // repaints), same as any scroll-driven React state should be.
  const [isNearBottom, setIsNearBottom] = useState(true);

  // ChatArea's scroll container is remounted on every thread switch (it's
  // keyed on conversationId in Chat.js), which replaces the DOM node
  // chatBoxRef points at - re-run this whenever conversationId changes so
  // the listener re-attaches to the new node instead of leaking a listener
  // on a detached one and never tracking scroll again.
  useEffect(() => {
    const element = chatBoxRef.current;
    if (!element) return undefined;
    let rafId = null;
    const handleScroll = () => {
      const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
      isNearBottomRef.current = distance <= NEAR_BOTTOM_THRESHOLD_PX;
      // Read the ref (not a value captured here) inside the rAF callback,
      // so if several scroll events land in the same frame the UI state
      // reflects the latest one when the frame fires, not whichever
      // triggered the first scheduling.
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          setIsNearBottom(isNearBottomRef.current);
        });
      }
    };
    handleScroll();
    element.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      element.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [conversationId]);

  // Single scroll mechanism - previously this set scrollTop directly AND
  // called scrollTo({behavior:'smooth'}) in the same frame, which fought
  // itself, and ran on every streamed token, restarting the smooth
  // animation dozens of times a second. Instant ('auto') by default: that's
  // the right choice for the per-token auto-follow below, where a "smooth"
  // scroll would visibly restart on every token; prefers-reduced-motion is
  // always honored regardless of what a caller asks for.
  const scrollToBottom = useCallback((behavior = 'auto') => {
    const element = chatBoxRef.current;
    if (!element) return;
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    element.scrollTo({
      top: element.scrollHeight,
      behavior: prefersReducedMotion ? 'auto' : behavior
    });
  }, []);

  // Explicit "jump to latest" action (the floating pill in ChatArea) -
  // updates both the ref (read synchronously by the auto-scroll effect
  // below) and the state (drives the pill's visibility) immediately, rather
  // than waiting on the next scroll event's rAF round-trip to hide the
  // pill, and does a deliberate smooth scroll since this is a one-off,
  // intentional jump - not the per-token case scrollToBottom's 'auto'
  // default exists for.
  const jumpToLatest = useCallback(() => {
    isNearBottomRef.current = true;
    setIsNearBottom(true);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  // Auto-scroll when chat history changes (a new message, or another token
  // streamed into the last one) - but only while the reader is already
  // pinned near the bottom, so a reply streaming in never yanks someone back
  // down while they've scrolled up to read earlier messages.
  useEffect(() => {
    if (isNearBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [chatHistory, scrollToBottom]);

  // Fetch this thread's messages whenever the active conversation changes.
  // Reset chatHistory synchronously first so switching threads never shows
  // a flash of the previous thread's messages while the new fetch is in flight.
  useEffect(() => {
    if (!conversationId) return;
    setChatHistory([]);

    const loadHistory = async () => {
      try {
        const res = await authFetch(`${config.API_BASE_URL}/chats/${conversationId}/messages`);
        const data = await handleApiResponse(res, navigate);
        if (data === null) return; // redirected to signin

        // Process the messages to handle image results and document metadata
        const processedMessages = data.map(msg => {
          try {
            // Try to parse content as JSON (for image results)
            const parsedContent = JSON.parse(msg.content);
            if (parsedContent.type === 'image_result') {
              // The server now writes this content itself once a job
              // completes (see ImageGenerationService.
              // _persist_assistant_message) using the job's raw image_url,
              // which may be relative (e.g. local disk storage) rather than
              // absolute (e.g. GCS) - the same normalization the live
              // polling path (pollImageGeneration) applies before it's ever
              // rendered.
              let imageUrl = parsedContent.imageUrl;
              if (imageUrl && !imageUrl.startsWith('http')) {
                imageUrl = `${config.API_BASE_URL}${imageUrl}`;
              }
              return {
                ...msg,
                content: parsedContent.content,
                imageUrl,
                isImageResult: true,
                imagePrompt: parsedContent.imagePrompt,
                jobId: parsedContent.jobId
              };
            }
          } catch (e) {
            // Not JSON, return as regular message
          }

          // Return message with document metadata if available
          return {
            ...msg,
            document_id: msg.document_id,
            document_url: msg.document_url,
            document_filename: msg.document_filename,
            document_type: msg.document_type,
            is_document_result: msg.is_document_result || false
          };
        });

        setChatHistory(processedMessages);
        // Scroll to bottom after loading history
        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        console.error('Error fetching chat history:', error);
        showMessage(`Failed to load chat history: ${describeApiError(error)}`, "error");
      }
    };

    loadHistory();
  }, [conversationId, navigate]);

  const showMessage = (msg, type = "error") => {
    setMessage(msg);
    setMessageType(type);
    
    // Clear the message after 3 seconds
    setTimeout(() => {
      setMessage("");
      setMessageType("error");
    }, 3000);
  };

  // Deletes the current chat thread (soft-delete, mirrors the sidebar's
  // per-thread delete). Replaces the old "wipe everything" behavior now
  // that chats are split into separate threads.
  //
  // Deliberately no confirmation prompt in here - this stays a pure "just do
  // it" action so the hook doesn't own any UI. The confirmation dialog
  // (ConfirmDeleteChatDialog) lives in ChatHeader.js, the component that
  // triggers this via its "Clear Chat" menu item, and only calls this once
  // the user has confirmed.
  const clearChatHistory = async () => {
    if (!conversationId) return;
    try {
      const response = await authFetch(`${config.API_BASE_URL}/chats/${conversationId}`, {
        method: 'DELETE',
      });

      const data = await handleApiResponse(response, navigate);
      if (data === null) return; // redirected to signin

      setChatHistory([]);
      showMessage("Chat deleted successfully", "success");
      navigate('/chat');
    } catch (error) {
      console.error('Error deleting chat:', error);
      showMessage(`Failed to delete chat: ${describeApiError(error)}`, "error");
    }
  };

  return {
    chatHistory,
    setChatHistory,
    message,
    messageType,
    chatBoxRef,
    scrollToBottom,
    isNearBottom,
    jumpToLatest,
    showMessage,
    clearChatHistory,
    navigate
  };
};
